import {
  PaginationRequest,
  UserFilterDTO,
  UserFormCreationDTO,
  UserFormDTO
} from '@vaneauneuf/dtos'
import Button from 'antd/lib/button'
import Modal from 'antd/lib/modal'
import Table, { ColumnProps, PaginationConfig, SorterResult } from 'antd/lib/table'
import React, { Dispatch } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { IRootState } from '../../../redux'
import { deleteUsers, fetchContacts } from '../../../redux/user'
import getMeetingSupportLabel from '../../../utils/getMeetingSupportLabel'

interface IState {
  pagination: PaginationConfig
  sortColumn: string
  sortOrder: 'ascend' | 'descend'
  selectedRowKeys: string[]
}

interface IContactTableProps {
  filters: UserFilterDTO
  onUpdateFilters: Dispatch<React.SetStateAction<UserFilterDTO & PaginationRequest>>
  tableTitle: React.ReactNode
  itemsPerPage: number
}

type IProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  IContactTableProps

class ContactsTable extends React.Component<IProps, IState> {
  public state: IState = {
    pagination: {
      total: this.props.total,
      pageSize: this.props.itemsPerPage
    },
    sortColumn: 'createDate',
    sortOrder: 'descend',
    selectedRowKeys: []
  }

  private columns: Array<ColumnProps<UserFormDTO>> = [
    {
      title: 'Email',
      key: 'email',
      render: contact => (contact.email ? contact.email : '')
    },
    {
      title: 'Nom',
      key: 'name',
      dataIndex: 'name',
      render: (contact, record) => `${record.lastName || ''} ${record.firstName || ''}`
    },
    {
      title: 'Téléphone',
      render: contact => (contact.phone ? contact.phone : '')
    },
    {
      title: 'Membre ?',
      render: contact => (contact.isMember ? 'Oui' : 'Non')
    },
    {
      title: 'Date de création',
      dataIndex: 'createDate',
      render: (contact, record) => new Date(record.createDate).toLocaleString(),
      sorter: true,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Disponibilités',
      render: contact => {
        const dispo = []
        const contactForm = contact.forms.find((f: UserFormCreationDTO) =>
          f.formData.hasOwnProperty('now')
        )
        const data = contactForm ? contactForm.formData : {}
        if (data.now) dispo.push('Immédiatement')
        if (data.morning) dispo.push('Matinée')
        if (data.afternoon) dispo.push('Après-midi')
        if (data.dayEnd) dispo.push('Soirée')
        return dispo.join(', ')
      }
    },
    {
      title: 'Accepte offres',
      render: contact => {
        const contactForm = contact.forms.find((f: any) => f.formData.offers)
        const data = contactForm ? contactForm.formData : {}
        return data.offers ? 'Oui' : 'Non'
      }
    },
    {
      title: 'Nb formulaires remplis',
      render: contact => (contact.forms ? contact.forms.length : 0)
    },
    {
      title: 'Rdv(s) demandé(s)',
      render: contact => {
        // return last contactForm with meeting filled by user
        const meetingForms = contact.forms.filter((f: any) => f.formData.meetingDate)

        if (meetingForms.length === 0) return 'Non'

        return `${getMeetingSupportLabel(meetingForms[0].formData.support)} ${new Date(
          meetingForms[0].formData.meetingDate
        ).toLocaleString()} `
      }
    },
    {
      title: 'Détails',
      dataIndex: 'id',
      render: (name, record) => <Link to={'/contacts/' + record.id}>Voir plus</Link>
    },
    {
      title: 'Action',
      render: (text, record) => <a onClick={() => this.handleDelete([record.id])}>Supprimer</a>
    }
  ]

  public async componentDidUpdate(prevProps: IProps) {
    if (
      prevProps.itemsPerPage !== this.props.itemsPerPage ||
      prevProps.total !== this.props.total ||
      (prevProps.removing && !this.props.removing)
    ) {
      if (prevProps.itemsPerPage !== this.props.itemsPerPage) {
        this.setState(state => ({ ...state, selectedRowKeys: [] }))
      }

      this.refresh(
        {
          ...this.state.pagination,
          total: this.props.total,
          pageSize: this.props.itemsPerPage
        },
        this.state.sortColumn,
        this.state.sortOrder
      )
    }
  }

  public async componentDidMount() {
    await this.props.fetchContacts({
      take: this.props.itemsPerPage,
      order: { [this.state.sortColumn]: this.state.sortOrder === 'ascend' ? 'ASC' : 'DESC' },
      onlyContact: true
    })
  }

  public render() {
    const rowSelection = {
      selectedRowKeys: this.state.selectedRowKeys,
      preserveSelectedRowKeys: false,
      onChange: (selectedRowKeys: string[]) => {
        this.setState(state => ({ ...state, selectedRowKeys }))
      }
    }

    return (
      <>
        <Table
          columns={[
            {
              children: this.columns.map((col: ColumnProps<UserFormDTO>) => ({
                ...col,
                ...(col.dataIndex === this.state.sortColumn
                  ? {
                      sorter: true,
                      sortOrder: this.state.sortOrder
                    }
                  : {})
              })),
              align: 'left',
              title: (
                <div className="ant-row-flex ant-row-flex-space-between ant-row-flex-middle">
                  {this.state.selectedRowKeys.length > 0 ? (
                    <Button type="danger" onClick={this.promptRemoveSelection}>
                      Supprimer les contacts sélectionnés
                    </Button>
                  ) : (
                    <div />
                  )}
                  {this.props.tableTitle}
                </div>
              )
            }
          ]}
          dataSource={this.props.contacts}
          rowKey="id"
          pagination={this.state.pagination}
          onChange={this.handleTableChange}
          loading={this.props.loading}
          rowSelection={rowSelection as any}
        />
      </>
    )
  }

  private promptRemoveSelection = () => {
    const count = this.state.selectedRowKeys.length
    if (count === 0) {
      return
    }

    this.handleDelete(this.state.selectedRowKeys)
  }

  private handleDelete = (id: string[]) => {
    if (id.length === 0) {
      return
    }

    Modal.confirm({
      title:
        'Confirmez-vous la suppression de ' +
        (id.length === 1 ? 'ce contact' : `ces ${id.length} contacts`) +
        ' ?',
      onOk: async () => {
        await this.props.deleteUsers(id)
        this.setState(state => ({ ...state, selectedRowKeys: [] }))
      }
    })
  }

  private handleTableChange = (
    pagination: PaginationConfig,
    filters: any,
    sorter: SorterResult<UserFormDTO>
  ) => {
    this.refresh(pagination, sorter.columnKey, sorter.order)
  }

  private refresh = (
    pagination: PaginationConfig,
    sortColumn: string,
    sortOrder: 'ascend' | 'descend'
  ) => {
    this.setState({ pagination, sortOrder, sortColumn })
    const settings = {
      ...this.props.filters,
      skip: pagination.current ? (pagination.current - 1) * this.props.itemsPerPage : 0,
      take: this.props.itemsPerPage,
      order: { [sortColumn]: (sortOrder === 'ascend' ? 'ASC' : 'DESC') as 'ASC' | 'DESC' }
    }

    this.props.fetchContacts(settings)
    this.props.onUpdateFilters(settings)
  }
}

const mapStateToProps = (state: IRootState) => ({
  contacts: state.userState.contacts,
  total: state.userState.total,
  loading: state.userState.loading,
  removing: state.userState.removing
})

const mapDispatchToProps = (dispatch: ThunkDispatch<IRootState, {}, AnyAction>) => ({
  fetchContacts: (queryParams: UserFilterDTO & PaginationRequest) =>
    dispatch(fetchContacts(queryParams)),
  deleteUsers: (ids: string[]) => dispatch(deleteUsers(ids))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContactsTable)
